import React, { ReactNode } from 'react'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { Badge } from '../../common/Badge'
import { theme } from '../../../theme/theme'
import { i18nSocietyType } from '../../../i18n'

interface RoleBadgeProps {
  roleString: string
}

const RoleBadge = ({ roleString }: RoleBadgeProps): ReactNode => {
  return (
    <Badge
      padding="px-1.5"
      colorClass="bg-brandGreen/10"
      borderColorClass="border-brandGreen"
      textColorClass="text-black"
      text={roleString}
      className="h-5"
    />
  )
}

interface NamedRoleProps {
  roleString: string
  fullName: string
  societyType?: string
}

const NamedRole = ({
  roleString,
  fullName,
  societyType,
}: NamedRoleProps): ReactNode => {
  const { translate } = useAppTranslation()

  return (
    <div className="flex items-center gap-1">
      <RoleBadge roleString={roleString} />
      <p style={theme.textVariants.base}>
        {translate('society.postedBy', {
          context: i18nSocietyType(societyType),
        })}{' '}
        {fullName}
      </p>
    </div>
  )
}

interface PostAuthorProps {
  role: string
  fullName?: string
  societyType?: string
}

const AuthorText = ({
  role,
  fullName,
  societyType,
}: PostAuthorProps): ReactNode => {
  const { translate } = useAppTranslation()
  const context = i18nSocietyType(societyType)
  const adminLabel = translate('society.roles.admin', { context })
  const boardLabel = translate('society.roles.board', { context })

  switch (role) {
    case 'admin':
      return (
        <NamedRole
          roleString={adminLabel}
          fullName={fullName ?? ''}
          societyType={societyType}
        />
      )
    case 'admin-society':
      return <RoleBadge roleString={adminLabel} />
    case 'board-member':
      return (
        <NamedRole
          roleString={boardLabel}
          fullName={fullName ?? ''}
          societyType={societyType}
        />
      )
    case 'board-society':
      return <RoleBadge roleString={boardLabel} />
    default:
      return <p style={theme.textVariants.base}>{fullName ?? ''}</p>
  }
}

export const PostAuthor = ({
  role,
  fullName,
  societyType,
}: PostAuthorProps): ReactNode => {
  return (
    <div className="inline-block overflow-hidden text-ellipsis whitespace-nowrap">
      <AuthorText role={role} fullName={fullName} societyType={societyType} />
    </div>
  )
}
